import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Loader from 'Components/Forms/Loader';
import GettingStarted from 'FeatureModules/Home/Components/GettingStarted';
import HomeProductOverview from 'FeatureModules/Home/Components/HomeProductOverview';
import HomeRequestsOverview from 'FeatureModules/Home/Components/HomeRequestsOverview';
import HomeAnalytics from 'FeatureModules/Home/Components/HomeAnalytics';
import HomeNextSteps from 'FeatureModules/Home/Components/NextSteps/HomeNextSteps';
import DeniedRequestCard from 'FeatureModules/Home/Components/Requests/DeniedRequestCard';
import ProvidedRequestCard from 'FeatureModules/Home/Components/Requests/ProvidedRequestCard';
import useHomeData from 'FeatureModules/Home/hooks/useHomeData';
import usePageTitle from 'hooks/usePageTitle';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import { CardIllustration, EmptyStateCard } from 'DesignSystem';
import React, { Fragment, useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';

const SKIP_PARTNER_CARD_KEY = 'skipPartnerCard';

const Home: FC = () => {
  usePageTitle('Home');
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {
    loading,
    error,
    filteredProducts = [],
    productPublishedSingleLanguage,
    hasPublishedProduct = false,
    overallStatus,
    homeTitle,
    highlightedProduct,
    companyAnalyticsExist,
    showProductList,
    partnerRequests,
  } = useHomeData();
  const navigate = useNavigate();
  const localSkipPartner = localStorage.getItem(SKIP_PARTNER_CARD_KEY);
  const [skipPartnerCard, setSkipPartnerCard] = useState(
    localSkipPartner ? JSON.parse(localSkipPartner) : false
  );

  // skip first engagement card on the home screen and save data in localStorage so it doesn't show after reload
  const handleSkipPartnerCard = () => {
    localStorage.setItem(SKIP_PARTNER_CARD_KEY, JSON.stringify(true));
    setSkipPartnerCard(true);
  };

  if (loading) return <Loader />;

  if (error) return <ErrorState />;

  const displayPartnerCard = !skipPartnerCard && overallStatus === 'EMPTY';

  return (
    <Fragment>
      <PageContainer>
        <PageTitle title="Home" />
        {partnerRequests.length > 0 && (
          <Box mt={4}>
            <HomeRequestsOverview
              requests={partnerRequests}
              renderRequestCard={request => {
                if (request.requestStatus === 'PROVIDED')
                  return <ProvidedRequestCard request={request} key={request.id} />;

                if (request.requestStatus === 'DENIED')
                  return <DeniedRequestCard request={request} key={request.id} />;
              }}
              partnerUser={false}
            />
          </Box>
        )}

        <Box pt={2} pb={2}>
          <Box display="flex" flexDirection={mediumScreen ? 'column' : 'row'} gap={3}>
            <Box display="flex" flexDirection="column" flex={1}>
              {hasPublishedProduct ? (
                <Fragment>
                  <PageSubTitle title="Next Steps" mb={2} />
                  <HomeNextSteps publishedProduct={productPublishedSingleLanguage} />
                </Fragment>
              ) : (
                <Fragment>
                  <PageSubTitle title="Getting Started" mb={2} />
                  {displayPartnerCard ? (
                    <EmptyStateCard
                      onCreateItemClick={() => {
                        navigate({ pathname: `/partners`, search: `?${APP_QUERY_PARAMS.new}=1` });
                      }}
                      variant={EItemTypes.PARTNER}
                      title="First off, add your partners. It will lay the foundation for your tracing and transparency"
                      size={EAvailableSizes.LARGE}
                      data-testid="partners-getting-started-home-card"
                      data-testid-button="create-partner-btn-getting-started"
                      background={EBackgroundTypes.WORLD}
                      buttonText="Add partner"
                      onClickSecondaryButton={handleSkipPartnerCard}
                      secondaryButtonText="I will do that later"
                      renderIllustration={
                        <CardIllustration
                          image={EIllustrationTypes.LOCATIONS}
                          width={300}
                          right={25}
                          top={0}
                        />
                      }
                    />
                  ) : (
                    <GettingStarted
                      products={filteredProducts}
                      overallStatus={overallStatus}
                      homeTitle={homeTitle}
                      highlightedProduct={highlightedProduct}
                      loading={loading}
                    />
                  )}
                </Fragment>
              )}
            </Box>

            {showProductList && (
              <HomeProductOverview loading={loading} products={filteredProducts} />
            )}
          </Box>
        </Box>

        {companyAnalyticsExist && <HomeAnalytics />}
      </PageContainer>
    </Fragment>
  );
};

export default Home;
